// Declarative config for the React-owned module sub-controls (Phase 4).
//
// During Phase 3 every module's `.module-sub-controls` was built as an HTML
// string in the legacy `createModule` and wired by `attachModuleEvents`. Phase 4
// moves that into React: `<ModuleControls>` renders a `<BentoSelect>` per
// descriptor here, driven by the store. This config is the single source of
// truth for both the rendered controls AND the store's initial control values
// (`initialControlsFor`), so the two can never drift.
//
// It is rolled out one module group per PR (slice 1: the three tables; slice 2:
// the team-toggle group + the non-team simple modules). A title absent from
// CONTROLS_CONFIG keeps its legacy string-built controls.
//
// Each descriptor's options mirror the legacy `controlsHTML` exactly (same
// values, labels, and ORDER — the control-order rule in CLAUDE.md). The legacy
// default is usually the first option; where it isn't (e.g. Shots on Target
// defaults its window to Season), `defaultValue` names it. The team toggle is
// special: its options are the current fixture's two teams (see `kind: "team"`),
// resolved in <ModuleControls>, and its default (the home side) is seeded by
// `createModule`, not here.

import type { CompetitionSeason } from "@bento/shared";
import type { BentoSelectOption } from "./BentoSelect.js";
import type { ModuleControls } from "../store/module.js";

/** One control inside a module's `.module-sub-controls` — a `<BentoSelect>` by
 *  default, a toggle `<button>` when `kind: "viewButton"`, or a link/unlink
 *  `<button>` when `kind: "linkButton"`. */
export interface ControlDescriptor {
  /** Which `ModuleControls` field this control reads/writes. Omitted for a
   *  `linkButton` (its linked/unlinked state is ephemeral UI, not store state —
   *  the legacy link button was never captured by `saveState`/undo either). */
  field?: keyof ModuleControls;
  /** Extra class on the control, matching the legacy toggle/button class (e.g.
   *  "season-toggle", "btts-view-btn", "ref-link-btn") so the shared CSS + any
   *  DOM hooks apply. */
  className: string;
  /** Accessible name for the trigger (selects). */
  ariaLabel?: string;
  /** Static options (selects). Empty/omitted for `team`/`viewButton`/`linkButton`. */
  options?: BentoSelectOption[];
  /** "team" → options are the fixture's two teams, resolved at render time.
   *  "viewButton" → a two-state toggle button with icons (not a <select>).
   *  "linkButton" → a link/unlink toggle button (cross-module sync; see below).
   *  "scope" → the market scope select: Match + the fixture's two teams, but the
   *    option VALUES are the fixed home/away slot keys "Match"/"ARS"/"EVE" (the
   *    dummy-data scope keys — verbatim from legacy), only the LABELS are the
   *    fixture teams. Resolved at render time.
   *  "player" → the Player Movement `.player-toggle`: options derived at render
   *    time from the module's own `scope` + `marketType` (via `playerOptionsFor`,
   *    keyed by `marketKind`).
   *  "leagueSeason" → the League Table's season: the seasons we serve for the
   *    current league, newest first (`leagueSeasonOptions`). Default (null) = the
   *    current season. */
  kind?: "team" | "viewButton" | "linkButton" | "scope" | "player" | "leagueSeason";
  /** player only: which dataset the in-scope player list comes from. */
  marketKind?: "goals" | "cards";
  /** Default value when it isn't `options[0]` (e.g. Shots on Target → Season). */
  defaultValue?: string;
  // ── viewButton only ──
  /** The two toggle values `[default, alternate]` (e.g. ["donut","blocks"]).
   *  `active` (the alternate) is what the legacy `.active` class marks. */
  values?: [string, string];
  /** Button `title` attribute (e.g. "Switch view"; linkButton: the linked title). */
  title?: string;
  /** linkButton only: the `title` when unlinked (e.g. "Referee unlinked …"). */
  titleUnlinked?: string;
  /** The button's inner HTML — the icon `<span>`s / `<svg>`, verbatim from the
   *  legacy builder so the shared CSS (which toggles them on `.active`) matches. */
  iconsHtml?: string;
}

// ── Reusable option sets (verbatim from the legacy builder) ──────────────────
const TIMELINE_5_10_SEASON: BentoSelectOption[] = [
  { value: "5", label: "Last 5" },
  { value: "10", label: "Last 10" },
  { value: "Season", label: "Season" },
];
const HA_ALL_HOME_AWAY: BentoSelectOption[] = [
  { value: "All", label: "All" },
  { value: "Home", label: "Home" },
  { value: "Away", label: "Away" },
];
const SEASONS_3_5_ALL: BentoSelectOption[] = [
  { value: "3", label: "Last 3" },
  { value: "5", label: "Last 5" },
  { value: "All", label: "All" },
];
const RATE_PG_TOTAL: BentoSelectOption[] = [
  { value: "Per Game", label: "Per Game" },
  { value: "Total", label: "Total" },
];
const RATE_PG_PS: BentoSelectOption[] = [
  { value: "Per Game", label: "Per Game" },
  { value: "Per Season", label: "Per Season" },
];
// Short window (no Season) — Recent / Home-Away Form. Legacy default is Last 10.
const TIMELINE_5_10: BentoSelectOption[] = [
  { value: "5", label: "Last 5" },
  { value: "10", label: "Last 10" },
];
// Scorers/Assists list order Season → Last 5 → Last 10 (default Season).
const TIMELINE_SEASON_5_10: BentoSelectOption[] = [
  { value: "Season", label: "Season" },
  { value: "5", label: "Last 5" },
  { value: "10", label: "Last 10" },
];
const HA_HOME_AWAY: BentoSelectOption[] = [
  { value: "Home", label: "Home" },
  { value: "Away", label: "Away" },
];
const VIEW_ROLLING_ACTUAL: BentoSelectOption[] = [
  { value: "Rolling", label: "Rolling" },
  { value: "Actual", label: "Actual" },
];
const VIEW_OUTCOME_FREQ: BentoSelectOption[] = [
  { value: "Outcome", label: "Outcome" },
  { value: "Frequency", label: "Frequency" },
];
const SPLIT_SF_CF: BentoSelectOption[] = [
  { value: "SF", label: "Scores First" },
  { value: "CF", label: "Concedes First" },
];

// ── Reusable descriptors ─────────────────────────────────────────────────────
const TEAM: ControlDescriptor = {
  field: "team",
  className: "team-toggle",
  ariaLabel: "Team",
  options: [],
  kind: "team",
};
const timeline = (defaultValue?: string): ControlDescriptor => ({
  field: "timeline",
  className: "timeline-toggle",
  ariaLabel: "Time window",
  options: TIMELINE_5_10_SEASON,
  ...(defaultValue ? { defaultValue } : {}),
});
const HA: ControlDescriptor = {
  field: "ha",
  className: "ha-toggle",
  ariaLabel: "Home or away",
  options: HA_ALL_HOME_AWAY,
};
const SEASONS: ControlDescriptor = {
  field: "seasons",
  className: "seasons-toggle",
  ariaLabel: "Seasons",
  options: SEASONS_3_5_ALL,
};
// Short time window (Last 5 / Last 10), default Last 10 (not the first option).
const SHORT_TIMELINE: ControlDescriptor = {
  field: "timeline",
  className: "timeline-toggle",
  ariaLabel: "Time window",
  options: TIMELINE_5_10,
  defaultValue: "10",
};
const TS_TIMELINE: ControlDescriptor = {
  field: "timeline",
  className: "timeline-toggle",
  ariaLabel: "Time window",
  options: TIMELINE_SEASON_5_10,
};
const HA_HOMEAWAY: ControlDescriptor = {
  field: "ha",
  className: "ha-toggle",
  ariaLabel: "Home or away",
  options: HA_HOME_AWAY,
};
const VIEW_ROLLING: ControlDescriptor = {
  field: "view",
  className: "view-toggle",
  ariaLabel: "View",
  options: VIEW_ROLLING_ACTUAL,
};
const VIEW_OUTCOME: ControlDescriptor = {
  field: "view",
  className: "view-toggle",
  ariaLabel: "View",
  options: VIEW_OUTCOME_FREQ,
};
const SPLIT: ControlDescriptor = {
  field: "split",
  className: "split-toggle",
  ariaLabel: "Split",
  options: SPLIT_SF_CF,
};
// Short window (Last 5 / Last 10) defaulting to Last 5 (BTTS / Clean Sheets).
const TIMELINE_510_D5: ControlDescriptor = {
  field: "timeline",
  className: "timeline-toggle",
  ariaLabel: "Time window",
  options: TIMELINE_5_10,
};

// ── View-mode toggle buttons (icons verbatim from the legacy builder) ─────────
// Both icon <span>s are always rendered; the shared CSS shows one based on the
// button's `.active` class (set when the value === the second/alternate value).
const ICONS_GRID_DONUT =
  '<span class="btts-icon-grid"><svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor"><rect x="0" y="0" width="5" height="5" rx="0.5"/><rect x="7" y="0" width="5" height="5" rx="0.5"/><rect x="0" y="7" width="5" height="5" rx="0.5"/><rect x="7" y="7" width="5" height="5" rx="0.5"/></svg></span>' +
  '<span class="btts-icon-donut"><svg width="14" height="14" viewBox="1 1 10 10" fill="currentColor"><path fill-rule="evenodd" d="M6 1a5 5 0 1 1 0 10A5 5 0 0 1 6 1zm0 3a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/></svg></span>';
const ICONS_HTFT =
  '<span class="htft-icon-bars"><svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor"><rect x="0" y="4" width="3" height="8" rx="0.5"/><rect x="4.5" y="1" width="3" height="11" rx="0.5"/><rect x="9" y="6" width="3" height="6" rx="0.5"/></svg></span>' +
  '<span class="htft-icon-flow"><svg width="14" height="14" viewBox="0 0 12 12" fill="currentColor"><rect x="0" y="1" width="2.5" height="4" rx="0.5"/><rect x="0" y="7" width="2.5" height="4" rx="0.5"/><rect x="9.5" y="0.5" width="2.5" height="11" rx="0.5"/><path d="M2.5,1.5 C6,1.5,6,0.5,9.5,0.5 L9.5,6 C6,6,6,5,2.5,5 Z" opacity="0.55"/><path d="M2.5,7.5 C6,7.5,6,6,9.5,6 L9.5,11.5 C6,11.5,6,11,2.5,11 Z" opacity="0.55"/></svg></span>';
const ICONS_HG =
  '<span class="hg-icon-bars"><svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0" y="1" width="9" height="3" rx="0.5"/><rect x="0" y="5.5" width="12" height="3" rx="0.5"/><rect x="0" y="10" width="6" height="3" rx="0.5"/></svg></span>' +
  '<span class="hg-icon-dots"><svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="0.4" y="5.5" width="1.2" height="3" rx="0.6"/><rect x="2.1" y="4" width="1.2" height="6" rx="0.6"/><rect x="3.8" y="2" width="1.2" height="10" rx="0.6"/><rect x="5.5" y="0.5" width="1.2" height="13" rx="0.6"/><rect x="7.2" y="2.5" width="1.2" height="9" rx="0.6"/><rect x="8.9" y="4.5" width="1.2" height="5" rx="0.6"/><rect x="10.6" y="3" width="1.2" height="8" rx="0.6"/><rect x="12.3" y="5" width="1.2" height="4" rx="0.6"/></svg></span>';

const viewButton = (
  className: string,
  values: [string, string],
  iconsHtml: string
): ControlDescriptor => ({
  field: "viewMode",
  className,
  kind: "viewButton",
  title: "Switch view",
  values,
  iconsHtml,
});
const rate = (options: BentoSelectOption[]): ControlDescriptor => ({
  field: "rate",
  className: "rate-toggle",
  ariaLabel: "Rate",
  options,
});

// ── Referee family (link button + ref selector + refSeason) ──────────────────
// The four dummy referees and the ref-season windows, verbatim from the legacy
// builder. The `.ref-link-btn` chain icon is a single <svg> (unlike the two-icon
// view buttons); the shared CSS greys it when `.unlinked`.
const REF_OPTIONS: BentoSelectOption[] = [
  { value: "M. Oliver", label: "M. Oliver" },
  { value: "A. Taylor", label: "A. Taylor" },
  { value: "S. Attwell", label: "S. Attwell" },
  { value: "C. Pawson", label: "C. Pawson" },
];
const REF_SEASON_OPTIONS: BentoSelectOption[] = [
  { value: "This Season", label: "This Season" },
  { value: "Last Season", label: "Last Season" },
  { value: "All Time", label: "All Time" },
];
const REF_LINK_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>';
const REF_LINK: ControlDescriptor = {
  className: "ref-link-btn",
  kind: "linkButton",
  title: "Sync referee across modules",
  titleUnlinked: "Referee unlinked — click to re-link",
  iconsHtml: REF_LINK_ICON,
};
const REF: ControlDescriptor = {
  field: "ref",
  className: "ref-toggle",
  ariaLabel: "Referee",
  options: REF_OPTIONS,
};
const REF_SEASON: ControlDescriptor = {
  field: "refSeason",
  className: "ref-season-toggle",
  ariaLabel: "Referee season",
  options: REF_SEASON_OPTIONS,
};

// ── Market family (bookmaker link + bookmaker/scope/line/type/player) ─────────
// All 10 market modules, verbatim from the legacy builder. The `.bk-link-btn`
// reuses the same chain <svg> as the referee link button. `scope` is a special
// select (kind:"scope") — its VALUES are the fixed dummy-data slot keys
// "Match"/"ARS"/"EVE", its LABELS the fixture teams. `player` is derived at
// render time (kind:"player") from the module's own scope + market type.
const BOOKMAKER_OPTIONS: BentoSelectOption[] = [
  { value: "Pinnacle", label: "Pinnacle" },
  { value: "Betfair", label: "Betfair" },
  { value: "Ladbrokes", label: "Ladbrokes" },
  { value: "Paddy Power", label: "Paddy Power" },
];
const MARKET_LINE_GOALS: BentoSelectOption[] = [
  { value: "under_0.5", label: "U/0.5" }, { value: "over_0.5", label: "O/0.5" },
  { value: "under_1.5", label: "U/1.5" }, { value: "over_1.5", label: "O/1.5" },
  { value: "under_2.5", label: "U/2.5" }, { value: "over_2.5", label: "O/2.5" },
  { value: "under_3.5", label: "U/3.5" }, { value: "over_3.5", label: "O/3.5" },
  { value: "under_4.5", label: "U/4.5" }, { value: "over_4.5", label: "O/4.5" },
];
// Cards movement stops at over_3.5 (no 4.5 line) — the first 8 goals lines.
const MARKET_LINE_CARDS: BentoSelectOption[] = MARKET_LINE_GOALS.slice(0, 8);
const MARKET_LINE_WINNER: BentoSelectOption[] = [
  { value: "home", label: "Home Win" },
  { value: "draw", label: "Draw" },
  { value: "away", label: "Away Win" },
];
const MARKET_TYPE_GOALS: BentoSelectOption[] = [
  { value: "Anytime", label: "Anytime" },
  { value: "First", label: "First" },
  { value: "2+", label: "2+" },
];
const MARKET_TYPE_CARDS: BentoSelectOption[] = [
  { value: "Anytime", label: "Anytime" },
  { value: "First", label: "First" },
];
const BK_LINK: ControlDescriptor = {
  className: "bk-link-btn",
  kind: "linkButton",
  title: "Sync bookmaker across modules",
  titleUnlinked: "Bookmaker unlinked — click to re-link",
  iconsHtml: REF_LINK_ICON, // same chain icon as the referee link button
};
const BOOKMAKER: ControlDescriptor = {
  field: "bookmaker",
  className: "bookmaker-toggle",
  ariaLabel: "Bookmaker",
  options: BOOKMAKER_OPTIONS,
};
const SCOPE: ControlDescriptor = {
  field: "scope",
  className: "market-scope-toggle",
  ariaLabel: "Market scope",
  kind: "scope",
  defaultValue: "Match",
};
const marketLine = (options: BentoSelectOption[]): ControlDescriptor => ({
  field: "marketLine",
  className: "market-line-toggle",
  ariaLabel: "Line",
  options,
});
const marketType = (options: BentoSelectOption[]): ControlDescriptor => ({
  field: "marketType",
  className: "market-type-toggle",
  ariaLabel: "Market type",
  options,
});
const playerControl = (marketKind: "goals" | "cards"): ControlDescriptor => ({
  field: "player",
  className: "player-toggle",
  ariaLabel: "Player",
  kind: "player",
  marketKind,
});

/** Title → ordered list of controls. Order matches the legacy builder. */
export const CONTROLS_CONFIG: Record<string, ControlDescriptor[]> = {
  // ── Slice 1: the tables ─────────────────────────────────────────────────────
  // League Table: its own season dropdown, to look back at earlier seasons'
  // tables (Ben, docs/QUESTIONS.md Q-003). The top bar has no season picker.
  "League Table": [
    { field: "season", className: "season-toggle", ariaLabel: "Season", kind: "leagueSeason" },
  ],
  "Form Table": [HA, timeline()],
  "xG Table": [rate(RATE_PG_PS), timeline()],

  // ── Slice 2: the team-toggle group + non-team simple modules ────────────────
  // Team only.
  "Penalty Takers": [TEAM],
  "Injuries & Suspension": [TEAM],
  "Doubtful Players": [TEAM],
  "News & Sentiment": [TEAM],
  // Team + time window.
  "Shots on Target — Club": [TEAM, timeline("Season")],
  "Goals by Time Band": [TEAM, timeline()],
  "Set Piece Goals": [TEAM, timeline()],
  "Over / Under Rates": [TEAM, timeline()],
  "Player Discipline": [TEAM, timeline()],
  // Team + time window + rate (own legacy branch).
  "Team Discipline": [TEAM, timeline(), rate(RATE_PG_TOTAL)],
  // No team.
  "Head to Head": [HA],
  "Head to Head Trends": [SEASONS],
  "Scored & Conceded": [rate(RATE_PG_TOTAL), timeline()],
  "Penalty Record": [rate(RATE_PG_TOTAL), timeline()],

  // ── Slice 3: remaining select-only modules ─────────────────────────────────
  // Form badges — short window (default Last 10); Home/Away adds a Home/Away venue.
  "Recent Form": [SHORT_TIMELINE],
  "Home/Away Form": [HA_HOMEAWAY, SHORT_TIMELINE],
  // Ranked bars — venue + a Season/Last-5/Last-10 window (default Season); Club adds team.
  "Top Scorers — League": [HA, TS_TIMELINE],
  "Assists — League": [HA, TS_TIMELINE],
  "Top Scorers — Club": [TEAM, HA, TS_TIMELINE],
  "Assists — Club": [TEAM, HA, TS_TIMELINE],
  // Donut with split + view (Outcome/Frequency), preceded by the time window.
  "First Goal": [timeline(), SPLIT, VIEW_OUTCOME],
  // xG charts — team + window + a Rolling/Actual view.
  "Performance Trajectory": [TEAM, timeline(), VIEW_ROLLING],
  "xG Timeline": [TEAM, timeline(), VIEW_ROLLING],
  // Radial clock — team, window, THEN venue (its ha is a late legacy branch).
  "Match States": [TEAM, timeline(), HA],

  // ── Slice 4: view-button group (a select pair + a two-state toggle button) ──
  "BTTS": [HA, TIMELINE_510_D5, viewButton("btts-view-btn", ["donut", "blocks"], ICONS_GRID_DONUT)],
  "Clean Sheets": [HA, TIMELINE_510_D5, viewButton("cs-view-btn", ["donut", "blocks"], ICONS_GRID_DONUT)],
  "HT/FT": [TEAM, timeline(), viewButton("htft-view-btn", ["flow", "bars"], ICONS_HTFT)],
  "1st/2nd Half Goals": [TEAM, timeline("Season"), viewButton("hg-view-btn", ["bands", "bars"], ICONS_HG)],

  // ── Slice 5: referee family (link button + cross-module ref sync) ───────────
  // Order matches the legacy builder: the link button leads, then the ref
  // selector (this differs from the canonical team-first order, but faithfully
  // reproduces the legacy `controlsHTML`). The `.ref-link-btn` toggles a
  // per-module linked/unlinked state; while linked, changing the referee syncs
  // it across all other linked referee modules — that propagation lives in
  // `main.js` (over the store), triggered via the `onControlChange` seam.
  "Referee Profile": [REF_LINK, REF, rate(RATE_PG_TOTAL)],
  "Referee x Club": [REF_LINK, REF, TEAM, REF_SEASON],
  "Referee Trend": [REF_LINK, REF],

  // ── Slice 6: market family (link button + cross-module bookmaker/scope sync) ─
  // Order matches the legacy builder: the bk-link button leads, then bookmaker,
  // then (where present) scope, then the line/type/player select. The bookmaker
  // "link" syncs across ALL linked market modules; scope/marketType sync only to
  // the static↔movement pair; the movement's line/player highlights the paired
  // static's [data-line]/[data-player] row. All of that propagation lives in
  // `main.js` (over the store), via the `onControlChange` seam + `syncPairOnDrop`.
  "Match Goals": [BK_LINK, BOOKMAKER, SCOPE],
  "Match Cards": [BK_LINK, BOOKMAKER, SCOPE],
  "Match Goals - Movement": [BK_LINK, BOOKMAKER, SCOPE, marketLine(MARKET_LINE_GOALS)],
  "Match Cards - Movement": [BK_LINK, BOOKMAKER, SCOPE, marketLine(MARKET_LINE_CARDS)],
  "Player Goals": [BK_LINK, BOOKMAKER, SCOPE, marketType(MARKET_TYPE_GOALS)],
  "Player Cards": [BK_LINK, BOOKMAKER, SCOPE, marketType(MARKET_TYPE_CARDS)],
  "Player Goals - Movement": [BK_LINK, BOOKMAKER, SCOPE, playerControl("goals")],
  "Player Cards - Movement": [BK_LINK, BOOKMAKER, SCOPE, playerControl("cards")],
  "Winner (1x2)": [BK_LINK, BOOKMAKER],
  "Winner (1x2) - Movement": [BK_LINK, BOOKMAKER, marketLine(MARKET_LINE_WINNER)],
};

/** Does this module's `.module-sub-controls` render via React yet? */
export function hasReactControls(title: string): boolean {
  return title in CONTROLS_CONFIG;
}

/**
 * The initial control values the store should hold for a React-controls module
 * — each descriptor's `defaultValue` or first option (the legacy default).
 * `createModule` merges this into the module's stored controls so the body,
 * which now reads control values from the store, starts with the right values.
 * The team toggle is skipped: its default (the home side) depends on the
 * fixture, and `createModule` already seeds it (`initialTeam`).
 */
/** The League Table's season options: every season we serve for `competitionId`,
 *  newest first, labelled as the season ("2026/27"). The first is the current one. */
export function leagueSeasonOptions(
  competitions: readonly CompetitionSeason[],
  competitionId: number
): BentoSelectOption[] {
  return competitions
    .filter((c) => c.competitionId === competitionId)
    .map((c) => c.season)
    .sort((a, b) => b.localeCompare(a))
    .map((season) => ({ value: season, label: season }));
}

export function initialControlsFor(title: string): Partial<ModuleControls> {
  const descriptors = CONTROLS_CONFIG[title];
  if (!descriptors) return {};
  const out: Partial<ModuleControls> = {};
  for (const d of descriptors) {
    if (d.kind === "team") continue; // seeded by createModule (fixture home side)
    if (d.kind === "linkButton") continue; // ephemeral UI state, no store field
    if (d.kind === "player") continue; // derived at render (fixture+scope+type), no static default
    if (d.kind === "leagueSeason") continue; // null = follow the current season
    if (!d.field) continue;
    if (d.kind === "viewButton") {
      out[d.field] = d.values?.[0] ?? null; // the default (non-active) view
      continue;
    }
    out[d.field] = d.defaultValue ?? d.options?.[0]?.value ?? null;
  }
  return out;
}
